"use client";

import {
  cn,
  Icon,
  type MarketIconSymbol,
  Select,
  SelectGroup,
  SelectItem,
  SelectLabel,
  Typography,
} from "@kyan/ui";

import { marketList } from "#/config/markets";
import { useMarketPairTickers } from "#/services/kyan/hooks/useMarketPairTicker";
import { useIndexPrices } from "#/store/app/indexPrices/indexPrices";
import { useMarket } from "#/store/app/portfolio/market";
import { formatCurrency, formatPercent } from "#/utils/valueFormatters";

import { LoadOrRender } from "../LoadOrRender/LoadOrRender";
import styles from "./MarketDropdown.module.scss";
import type { MarketDropdownType } from "./MarketDropdown.types";
import { isPositive } from "./utils";

export interface MarketDropdownProps {
  type: MarketDropdownType;
  className?: string;
}

export const MarketDropdown: React.FC<MarketDropdownProps> = ({
  type,
  className,
}) => {
  const { market, setMarketByUnderlying } = useMarket();
  const { indexPrices } = useIndexPrices();
  const { data: marketPairTickers, isLoading: isTickersLoading } =
    useMarketPairTickers();

  const isPerps = type === "perps";

  const currencyText = isPerps ? "PERPS" : "OPTIONS";
  const marketType = isPerps ? "PERPETUALS" : "OPTION CONTRACTS";
  const priceText = "INDEX PRICE";

  return (
    <div
      className={cn(
        "w-80 min-w-80 rounded-md p-px sm:w-101.25 sm:min-w-101.25 [&span]:w-full",
        {
          [styles.gradientBorderOptions]: !isPerps,
          [styles.gradientBorderPerps]: isPerps,
        },
        className,
      )}
    >
      <Select
        value={market.underlying}
        iconClassName="group-hover:text-textPrimary dark:group-hover:text-white"
        triggerClassName="group dark:bg-buttonBg hover:bg-buttonHover dark:bg-buttonFill dark:aria-expanded:bg-buttonFill h-[50px] rounded-[5px] border-0 bg-white shadow-[0px_2px_14px_0px_#1a1a1a24] aria-expanded:border-b-transparent aria-expanded:bg-white"
        contentClassName={cn(
          "border-divider animated-dropdown relative -top-1 -left-px w-[calc(var(--radix-popper-anchor-width)+1px)] rounded-tl-none rounded-tr-none border-0 shadow-[0px_5px_8px_rgba(201,201,201,0.50)] dark:w-[calc(var(--radix-popper-anchor-width)+1.5px)] dark:shadow-none",
        )}
        valueComponent={
          <div
            data-testid="market-dropdown"
            className="flex w-full items-center gap-2.5"
          >
            <Icon
              name={market.underlying.toLowerCase() as MarketIconSymbol}
              className="text-textPrimary flex size-6 items-center justify-center"
            />

            <div className="flex w-full justify-between pr-4">
              <div className="flex flex-col gap-1 text-start">
                <Typography grotesk primary>
                  {market.underlying}-{currencyText}
                </Typography>
                <Typography medium size={10} variant="secondary">
                  {marketType}
                </Typography>
              </div>
              <div className="flex flex-col gap-1 text-end">
                <Typography medium size={10} variant="secondary">
                  {priceText}
                </Typography>
                <LoadOrRender
                  className="h-3.5 w-20"
                  isLoading={Number.isNaN(indexPrices[market.underlying])}
                >
                  <Typography grotesk primary>
                    {formatCurrency(indexPrices[market.underlying])}
                  </Typography>
                </LoadOrRender>
              </div>
            </div>
          </div>
        }
        onValueChange={setMarketByUnderlying}
      >
        <SelectGroup className="w-full rounded-none">
          <SelectLabel className="bg-background dark:bg-buttonFill border-divider rounded-none border border-x-0 font-normal">
            <div className="flex justify-between pr-8.5 pl-1">
              <Typography medium size={12} className="ml-1" variant="secondary">
                Market
              </Typography>
              <Typography
                medium
                size={12}
                className="mr-0.5"
                variant="secondary"
              >
                Price
              </Typography>
            </div>
          </SelectLabel>
          {marketList.map((item) => {
            const marketPairTicker = marketPairTickers[item.pair];

            return (
              <SelectItem
                showCheck
                key={item.underlying}
                value={item.underlying}
                className={cn(
                  "bg-background dark:bg-buttonFill data-[state=checked]:hover:bg-buttonHover dark:data-[state=checked]:bg-buttonFill data-[state=checked]:bg-background group h-[42px] w-full pr-4 pl-3 last:rounded-b-[3px] last:border-b-0 [&>span]:w-full",
                )}
              >
                <div className="flex justify-between pr-7">
                  <div className="flex items-center gap-2">
                    <Icon
                      name={item.underlying.toLowerCase() as MarketIconSymbol}
                      className={cn(
                        "flex size-6 items-center justify-center opacity-50 transition-all group-hover:text-black group-hover:opacity-100 dark:group-hover:text-white",
                      )}
                    />

                    <Typography primary>
                      {item.underlying}-{currencyText}
                    </Typography>
                  </div>
                  <div className="flex flex-col text-right leading-[initial]">
                    <LoadOrRender
                      className="h-3.5 w-20"
                      isLoading={Number.isNaN(indexPrices[market.underlying])}
                    >
                      <Typography primary>
                        {formatCurrency(indexPrices[item.underlying])}
                      </Typography>
                    </LoadOrRender>
                    <LoadOrRender isLoading={isTickersLoading}>
                      <Typography
                        size={10}
                        className={cn(
                          isPositive(marketPairTicker.price_change_pct)
                            ? "text-success"
                            : "text-error",
                        )}
                      >
                        {formatPercent(
                          marketPairTicker.price_change_pct / 100,
                          {
                            precision: 2,
                          },
                        )}
                      </Typography>
                    </LoadOrRender>
                  </div>
                </div>
              </SelectItem>
            );
          })}
        </SelectGroup>
      </Select>
    </div>
  );
};
